/**
 * Career Pathways Mock Data
 *
 * This file contains mock data for the Career Pathways demo.
 * In production, this data will come from the tio-api backend via:
 *
 * API Endpoints (planned):
 *   GET /api/v2/career-roles              - List available roles
 *   GET /api/v2/career-roles/:id          - Role details with requirements
 *   GET /api/v2/employees/:id/skills      - Employee's current skills
 *   GET /api/v2/employees/:id/skill-gaps  - Gap analysis for target role
 *   GET /api/v2/employees/:id/recommendations - Recommended programs
 *   GET /api/v2/employees/:id/career-plans - Employee's career plans
 *
 * Data Models (planned):
 *   CareerRole, Skill, EmployeeSkill, CareerPlan, CareerPlanStep, ProgramSkill
 *
 * See: workspace/plans/reimagined-feature-analysis.md
 */

// ============================================================================
// EMPLOYEE PROFILE
// ============================================================================

export interface EmployeeProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  currentRole: CurrentRole;
  targetRole: TargetRole | null;
  taBalance: TaBenefit;
  skills: EmployeeSkill[];
  careerPlanProgress: number;
}

export interface CurrentRole {
  title: string;
  department: string;
  level: string;
  startDate: string;
  yearsInRole: number;
}

export interface TargetRole {
  id: string;
  title: string;
  targetDate: string;
  matchScore: number;
}

export interface TaBenefit {
  annualMax: number;
  used: number;
  remaining: number;
  eligiblePrograms: number;
}

export interface EmployeeSkill {
  id: string;
  name: string;
  category: 'technical' | 'soft' | 'certification';
  level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  proficiencyScore: number; // 0-100
  source: 'verified' | 'in-progress' | 'self-reported';
  verifiedDate?: string;
  verifiedBy?: string;
  progress?: number; // For in-progress skills
  targetDate?: string;
}

export const MOCK_EMPLOYEE: EmployeeProfile = {
  id: 'emp-001',
  firstName: 'Maria',
  lastName: 'Rodriguez',
  email: 'maria.rodriguez@memorial.health',
  currentRole: {
    title: 'Registered Nurse (RN)',
    department: 'Medical-Surgical Unit',
    level: 'Senior',
    startDate: '2021-03-15',
    yearsInRole: 4,
  },
  targetRole: {
    id: 'nurse-manager',
    title: 'Nurse Manager',
    targetDate: '2026-12-01',
    matchScore: 68,
  },
  taBalance: {
    annualMax: 5250,
    used: 1850,
    remaining: 3400,
    eligiblePrograms: 24,
  },
  skills: [
    {
      id: 'skill-rn',
      name: 'RN License',
      category: 'certification',
      level: 'expert',
      proficiencyScore: 100,
      source: 'verified',
      verifiedDate: '2021-03-15',
      verifiedBy: 'State Board of Nursing',
    },
    {
      id: 'skill-bsn',
      name: 'BSN Degree',
      category: 'certification',
      level: 'expert',
      proficiencyScore: 100,
      source: 'verified',
      verifiedDate: '2024-08-15',
      verifiedBy: 'University Partners Network',
    },
    {
      id: 'skill-epic',
      name: 'Epic EMR',
      category: 'technical',
      level: 'advanced',
      proficiencyScore: 85,
      source: 'verified',
      verifiedDate: '2024-06-20',
      verifiedBy: 'Internal Certification',
    },
    {
      id: 'skill-leadership',
      name: 'Leadership',
      category: 'soft',
      level: 'intermediate',
      proficiencyScore: 65,
      source: 'in-progress',
      progress: 65,
      targetDate: '2025-02-15',
    },
    {
      id: 'skill-epic-super',
      name: 'Epic Super User',
      category: 'technical',
      level: 'intermediate',
      proficiencyScore: 40,
      source: 'in-progress',
      progress: 40,
      targetDate: '2025-03-01',
    },
    {
      id: 'skill-communication',
      name: 'Patient Communication',
      category: 'soft',
      level: 'expert',
      proficiencyScore: 95,
      source: 'self-reported',
    },
    {
      id: 'skill-critical',
      name: 'Critical Thinking',
      category: 'soft',
      level: 'advanced',
      proficiencyScore: 80,
      source: 'self-reported',
    },
  ],
  careerPlanProgress: 68,
};

// ============================================================================
// CAREER ROLES
// ============================================================================

export interface CareerRole {
  id: string;
  title: string;
  family: string;
  familyIcon: string;
  level: 'Entry' | 'Mid' | 'Senior' | 'Management' | 'Executive';
  department: string;
  salaryMin: number;
  salaryMax: number;
  growthRate: number; // percentage
  description: string;
  responsibilities: string[];
  requiredSkills: RoleSkill[];
  preferredSkills: RoleSkill[];
  requiredCredentials: string[];
  preferredCredentials: string[];
  typicalPath: string[]; // Role IDs that typically lead to this role
  nextSteps: string[]; // Role IDs that typically follow this role
  companyOpenings: number;
  isHighDemand: boolean;
  matchScore?: number; // Calculated for current employee
}

export interface RoleSkill {
  skillId: string;
  name: string;
  proficiencyRequired: number; // 0-100
  isRequired: boolean;
}

export const CAREER_ROLES: CareerRole[] = [
  {
    id: 'nurse-manager',
    title: 'Nurse Manager',
    family: 'Nursing',
    familyIcon: 'first-aid',
    level: 'Management',
    department: 'Nursing Administration',
    salaryMin: 85000,
    salaryMax: 120000,
    growthRate: 18,
    description:
      'Oversees daily operations of a nursing unit, manages staff schedules, ensures quality patient care, and handles budget and resource allocation.',
    responsibilities: [
      'Manage nursing staff schedules and assignments',
      'Monitor and improve patient care quality metrics',
      'Develop and manage unit budget',
      'Conduct staff performance reviews',
      'Ensure compliance with healthcare regulations',
      'Coordinate with physicians and other departments',
    ],
    requiredSkills: [
      { skillId: 'leadership', name: 'Leadership', proficiencyRequired: 80, isRequired: true },
      {
        skillId: 'budget-mgmt',
        name: 'Budget Management',
        proficiencyRequired: 75,
        isRequired: true,
      },
      {
        skillId: 'staff-scheduling',
        name: 'Staff Scheduling',
        proficiencyRequired: 70,
        isRequired: true,
      },
      {
        skillId: 'quality-improvement',
        name: 'Quality Improvement',
        proficiencyRequired: 75,
        isRequired: true,
      },
    ],
    preferredSkills: [
      { skillId: 'epic', name: 'Epic EMR', proficiencyRequired: 70, isRequired: false },
      {
        skillId: 'conflict-resolution',
        name: 'Conflict Resolution',
        proficiencyRequired: 70,
        isRequired: false,
      },
    ],
    requiredCredentials: ['BSN Degree', 'RN License', '5+ years clinical experience'],
    preferredCredentials: ['MSN Degree', 'Nurse Leadership Certificate', 'Lean Six Sigma'],
    typicalPath: ['registered-nurse', 'charge-nurse'],
    nextSteps: ['director-nursing', 'cno'],
    companyOpenings: 8,
    isHighDemand: true,
    matchScore: 68,
  },
  {
    id: 'charge-nurse',
    title: 'Charge Nurse',
    family: 'Nursing',
    familyIcon: 'first-aid',
    level: 'Senior',
    department: 'Various Units',
    salaryMin: 72000,
    salaryMax: 95000,
    growthRate: 16,
    description:
      'Supervises nursing staff during a shift, coordinates patient assignments, handles immediate staffing issues, and serves as the point of contact for physicians.',
    responsibilities: [
      'Oversee shift operations and staff assignments',
      'Handle patient flow and bed management',
      'Coordinate with physicians and ancillary staff',
      'Respond to clinical emergencies',
      'Mentor and support nursing staff',
    ],
    requiredSkills: [
      { skillId: 'patient-care', name: 'Patient Care', proficiencyRequired: 85, isRequired: true },
      {
        skillId: 'team-leadership',
        name: 'Team Leadership',
        proficiencyRequired: 70,
        isRequired: true,
      },
      {
        skillId: 'clinical-protocols',
        name: 'Clinical Protocols',
        proficiencyRequired: 80,
        isRequired: true,
      },
    ],
    preferredSkills: [
      {
        skillId: 'crisis-management',
        name: 'Crisis Management',
        proficiencyRequired: 70,
        isRequired: false,
      },
    ],
    requiredCredentials: ['BSN Degree', 'RN License', '3+ years clinical experience'],
    preferredCredentials: ['Charge Nurse Training', 'ACLS Certification'],
    typicalPath: ['registered-nurse'],
    nextSteps: ['nurse-manager', 'clinical-educator'],
    companyOpenings: 15,
    isHighDemand: true,
    matchScore: 95,
  },
  {
    id: 'clinical-informatics-specialist',
    title: 'Clinical Informatics Specialist',
    family: 'Health Informatics',
    familyIcon: 'code',
    level: 'Mid',
    department: 'Health Information Technology',
    salaryMin: 75000,
    salaryMax: 110000,
    growthRate: 32,
    description:
      'Bridges the gap between clinical practice and technology by optimizing EMR workflows, training staff, and analyzing healthcare data to improve patient outcomes.',
    responsibilities: [
      'Design and optimize clinical workflows in Epic',
      'Train clinical staff on EMR systems',
      'Analyze healthcare data for quality improvement',
      'Support system implementations and upgrades',
      'Serve as liaison between clinical and IT teams',
    ],
    requiredSkills: [
      { skillId: 'epic', name: 'Epic EMR', proficiencyRequired: 85, isRequired: true },
      {
        skillId: 'data-analytics',
        name: 'Data Analytics',
        proficiencyRequired: 70,
        isRequired: true,
      },
      {
        skillId: 'clinical-workflows',
        name: 'Clinical Workflows',
        proficiencyRequired: 75,
        isRequired: true,
      },
    ],
    preferredSkills: [
      { skillId: 'sql', name: 'SQL', proficiencyRequired: 60, isRequired: false },
      { skillId: 'training', name: 'Training', proficiencyRequired: 70, isRequired: false },
    ],
    requiredCredentials: ['Clinical background (RN, RT, etc.)', 'Epic Certification'],
    preferredCredentials: ['Health Informatics Certificate', 'Masters in Health Informatics'],
    typicalPath: ['registered-nurse', 'unit-secretary'],
    nextSteps: ['informatics-manager', 'cmio'],
    companyOpenings: 5,
    isHighDemand: true,
    matchScore: 78,
  },
];

// ============================================================================
// SKILL GAPS
// ============================================================================

export interface SkillGap {
  id: string;
  skillId: string;
  name: string;
  importance: 'Critical' | 'Important' | 'Nice to Have';
  currentLevel: number;
  requiredLevel: number;
  targetRole: string;
  description: string;
  whyNeeded: string;
  timeToClose: string;
  learningPaths: LearningPath[];
}

export interface LearningPath {
  id: string;
  title: string;
  provider: string;
  type: 'Certificate' | 'Course' | 'Degree' | 'Workshop' | 'Training' | 'Program';
  duration: string;
  cost: string;
  costValue: number;
  taEligible: boolean;
  format: 'Online' | 'In-person' | 'Hybrid';
  matchScore: number;
  description: string;
  skillsGained: string[];
  reviews: { rating: number; count: number };
}

export const SKILL_GAPS: SkillGap[] = [
  {
    id: 'gap-budget',
    skillId: 'budget-mgmt',
    name: 'Budget Management',
    importance: 'Critical',
    currentLevel: 0,
    requiredLevel: 80,
    targetRole: 'Nurse Manager',
    description:
      'Understanding healthcare financial management, cost analysis, resource allocation, and budget planning for unit operations.',
    whyNeeded:
      'Nurse Managers are responsible for unit budgets ranging from $500K to $2M annually. This skill is essential for the role.',
    timeToClose: '3-4 months',
    learningPaths: [
      {
        id: 'path-hfm',
        title: 'Healthcare Financial Management Certificate',
        provider: 'ACHE',
        type: 'Certificate',
        duration: '8 weeks',
        cost: '$1,200',
        costValue: 1200,
        taEligible: true,
        format: 'Online',
        matchScore: 98,
        description:
          'Comprehensive program covering healthcare budgeting, cost analysis, and financial reporting.',
        skillsGained: [
          'Budget Planning',
          'Cost Analysis',
          'Financial Reporting',
          'Resource Allocation',
        ],
        reviews: { rating: 4.8, count: 342 },
      },
      {
        id: 'path-budget-basics',
        title: 'Nursing Unit Budget Basics',
        provider: 'Hospital Internal',
        type: 'Workshop',
        duration: '2 days',
        cost: 'Free',
        costValue: 0,
        taEligible: false,
        format: 'In-person',
        matchScore: 85,
        description: 'Internal workshop focused on practical budget management for nursing units.',
        skillsGained: ['Budget Basics', 'Expense Tracking'],
        reviews: { rating: 4.5, count: 89 },
      },
      {
        id: 'path-finance-hcp',
        title: 'Finance for Healthcare Professionals',
        provider: 'Coursera (University of Pennsylvania)',
        type: 'Course',
        duration: '6 weeks',
        cost: '$49/month',
        costValue: 147,
        taEligible: true,
        format: 'Online',
        matchScore: 82,
        description: 'University-level course on healthcare economics and financial management.',
        skillsGained: ['Healthcare Economics', 'Financial Analysis', 'Strategic Planning'],
        reviews: { rating: 4.6, count: 1250 },
      },
    ],
  },
  {
    id: 'gap-scheduling',
    skillId: 'staff-scheduling',
    name: 'Staff Scheduling',
    importance: 'Important',
    currentLevel: 20,
    requiredLevel: 75,
    targetRole: 'Nurse Manager',
    description:
      'Creating efficient nursing schedules, managing shift coverage, handling call-offs, and ensuring appropriate staffing ratios.',
    whyNeeded:
      'Proper staff scheduling directly impacts patient care quality, nurse satisfaction, and unit budget. Charge Nurses and Managers handle this daily.',
    timeToClose: '2-3 months',
    learningPaths: [
      {
        id: 'path-workforce',
        title: 'Healthcare Workforce Management',
        provider: 'AONL',
        type: 'Certificate',
        duration: '6 weeks',
        cost: '$800',
        costValue: 800,
        taEligible: true,
        format: 'Hybrid',
        matchScore: 95,
        description:
          'Covers scheduling software, staffing ratios, predictive scheduling, and handling coverage gaps.',
        skillsGained: [
          'Scheduling',
          'Staffing Ratios',
          'Workforce Planning',
          'Coverage Management',
        ],
        reviews: { rating: 4.7, count: 215 },
      },
      {
        id: 'path-charge-scheduling',
        title: 'Charge Nurse Training - Scheduling Module',
        provider: 'Hospital Internal',
        type: 'Training',
        duration: '1 week',
        cost: 'Free',
        costValue: 0,
        taEligible: false,
        format: 'In-person',
        matchScore: 90,
        description: 'Hands-on training with our scheduling system and coverage policies.',
        skillsGained: ['Internal Scheduling System', 'Coverage Policies'],
        reviews: { rating: 4.4, count: 67 },
      },
    ],
  },
  {
    id: 'gap-qi',
    skillId: 'quality-improvement',
    name: 'Quality Improvement',
    importance: 'Important',
    currentLevel: 35,
    requiredLevel: 85,
    targetRole: 'Nurse Manager',
    description:
      'Leading quality improvement initiatives, understanding healthcare metrics, implementing evidence-based practices, and driving patient safety.',
    whyNeeded:
      'Nurse Managers are expected to lead QI projects that improve patient outcomes and meet accreditation standards.',
    timeToClose: '4-5 months',
    learningPaths: [
      {
        id: 'path-lss',
        title: 'Lean Six Sigma Yellow Belt for Healthcare',
        provider: 'IHI',
        type: 'Certificate',
        duration: '8 weeks',
        cost: '$1,500',
        costValue: 1500,
        taEligible: true,
        format: 'Online',
        matchScore: 97,
        description:
          'Industry-standard quality improvement methodology adapted for healthcare settings.',
        skillsGained: [
          'Process Improvement',
          'Data Analysis',
          'Root Cause Analysis',
          'PDSA Cycles',
        ],
        reviews: { rating: 4.9, count: 890 },
      },
      {
        id: 'path-qi-nursing',
        title: 'Quality Improvement in Nursing',
        provider: 'American Nurses Association',
        type: 'Course',
        duration: '4 weeks',
        cost: '$350',
        costValue: 350,
        taEligible: true,
        format: 'Online',
        matchScore: 88,
        description: 'Nursing-specific QI methodologies and implementation strategies.',
        skillsGained: ['QI Methodology', 'Evidence-Based Practice', 'Outcome Measurement'],
        reviews: { rating: 4.6, count: 445 },
      },
      {
        id: 'path-qi-champion',
        title: 'Hospital QI Champion Program',
        provider: 'Hospital Internal',
        type: 'Program',
        duration: '3 months',
        cost: 'Free',
        costValue: 0,
        taEligible: false,
        format: 'Hybrid',
        matchScore: 85,
        description: 'Be mentored by our Quality department on a real QI project.',
        skillsGained: ['Project Leadership', 'QI Implementation', 'Change Management'],
        reviews: { rating: 4.8, count: 34 },
      },
      {
        id: 'path-hqa',
        title: 'Healthcare Quality Analytics',
        provider: 'edX (Harvard)',
        type: 'Course',
        duration: '10 weeks',
        cost: '$199',
        costValue: 199,
        taEligible: true,
        format: 'Online',
        matchScore: 78,
        description: 'Advanced analytics and measurement for healthcare quality.',
        skillsGained: ['Analytics', 'Statistical Methods', 'Benchmarking'],
        reviews: { rating: 4.5, count: 678 },
      },
    ],
  },
];

// ============================================================================
// CAREER PLAN
// ============================================================================

export interface CareerPlan {
  id: string;
  employeeId: string;
  targetRole: string;
  targetRoleTitle: string;
  targetDate: string;
  progress: number;
  status: 'active' | 'completed' | 'paused';
  createdAt: string;
  milestones: Milestone[];
}

export interface Milestone {
  id: string;
  title: string;
  type: 'certification' | 'course' | 'experience' | 'milestone';
  status: 'completed' | 'in-progress' | 'planned';
  description: string;
  targetDate?: string;
  completedDate?: string;
  progress?: number;
  programId?: string;
}

export const CAREER_PLAN: CareerPlan = {
  id: 'plan-001',
  employeeId: 'emp-001',
  targetRole: 'nurse-manager',
  targetRoleTitle: 'Nurse Manager',
  targetDate: '2026-12-01',
  progress: 68,
  status: 'active',
  createdAt: '2024-01-15',
  milestones: [
    {
      id: 'ms-1',
      title: 'Complete BSN Degree',
      type: 'certification',
      status: 'completed',
      completedDate: '2024-08-15',
      description: 'RN-to-BSN program via Tuition Assistance',
    },
    {
      id: 'ms-2',
      title: 'Nurse Leadership Certificate',
      type: 'course',
      status: 'in-progress',
      progress: 65,
      targetDate: '2025-02-15',
      description: 'ANCC Certificate - currently in Module 4',
    },
    {
      id: 'ms-3',
      title: 'Epic EMR Super User Training',
      type: 'course',
      status: 'in-progress',
      progress: 40,
      targetDate: '2025-03-01',
      description: 'Internal certification for EMR workflow optimization',
    },
    {
      id: 'ms-4',
      title: 'Complete Charge Nurse Training',
      type: 'certification',
      status: 'planned',
      targetDate: '2025-06-01',
      description: 'Internal program - required for management track',
    },
    {
      id: 'ms-5',
      title: 'Shadow Nurse Manager',
      type: 'experience',
      status: 'planned',
      targetDate: '2025-09-01',
      description: 'Job shadow with current unit manager for 2 weeks',
    },
    {
      id: 'ms-6',
      title: 'Apply for Nurse Manager Role',
      type: 'milestone',
      status: 'planned',
      targetDate: '2026-12-01',
      description: 'Target open Nurse Manager position in Med-Surg or ICU',
    },
  ],
};

// ============================================================================
// SUCCESS STORIES
// ============================================================================

export interface SuccessStory {
  id: string;
  employeeName: string;
  initials: string;
  avatarColor: string;
  fromRole: string;
  toRole: string;
  department: string;
  timeframe: string;
  salaryIncrease: string;
  quote: string;
  skillsGained: string[];
  programsCompleted: string[];
  featured: boolean;
  likes: number;
}

export const SUCCESS_STORIES: SuccessStory[] = [
  {
    id: 'story-1',
    employeeName: 'Maria R.',
    initials: 'MR',
    avatarColor: 'from-pink-400 to-rose-500',
    fromRole: 'CNA (Certified Nursing Assistant)',
    toRole: 'Registered Nurse (RN)',
    department: 'Medical-Surgical Unit',
    timeframe: '3 years',
    salaryIncrease: '+85%',
    quote:
      "I started as a CNA on the night shift. Career Pathways showed me the RN bridge program was covered by TA. Three years later, I'm an RN in the same unit where I started - now I'm mentoring new CNAs!",
    skillsGained: [
      'Patient Assessment',
      'Medication Administration',
      'Care Planning',
      'IV Therapy',
    ],
    programsCompleted: ['RN Bridge Program', 'NCLEX Prep Course'],
    featured: true,
    likes: 312,
  },
  {
    id: 'story-2',
    employeeName: 'James T.',
    initials: 'JT',
    avatarColor: 'from-blue-400 to-indigo-500',
    fromRole: 'Unit Secretary',
    toRole: 'Clinical Informatics Analyst',
    department: 'Health Information Technology',
    timeframe: '2 years',
    salaryIncrease: '+68%',
    quote:
      'I was good at our EMR system and helped train others. The AI Advisor suggested Clinical Informatics - I had no idea it was a career! Now I help design workflows that save nurses hours every day.',
    skillsGained: ['Epic EMR', 'Workflow Analysis', 'Training', 'SQL'],
    programsCompleted: ['Health Informatics Certificate', 'Epic Certification'],
    featured: true,
    likes: 245,
  },
  {
    id: 'story-3',
    employeeName: 'Priya S.',
    initials: 'PS',
    avatarColor: 'from-emerald-400 to-teal-500',
    fromRole: 'Staff RN (Med-Surg)',
    toRole: 'Charge Nurse',
    department: 'Medical-Surgical Unit',
    timeframe: '18 months',
    salaryIncrease: '+22%',
    quote:
      'The career ladder showed me exactly what leadership skills I needed. I completed the Nurse Leadership Certificate through TA, and my manager noticed immediately. Got promoted within 6 months!',
    skillsGained: ['Team Leadership', 'Conflict Resolution', 'Staffing', 'Quality Metrics'],
    programsCompleted: ['Nurse Leadership Certificate', 'Charge Nurse Training'],
    featured: false,
    likes: 198,
  },
  {
    id: 'story-4',
    employeeName: 'David M.',
    initials: 'DM',
    avatarColor: 'from-amber-400 to-orange-500',
    fromRole: 'Phlebotomist',
    toRole: 'Medical Laboratory Technician',
    department: 'Laboratory Services',
    timeframe: '2 years',
    salaryIncrease: '+52%',
    quote:
      'I loved working in the lab but wanted more responsibility. Career Pathways connected me with an MLT program that worked around my schedule. The hospital covered most of the tuition!',
    skillsGained: ['Lab Analysis', 'Quality Control', 'Specimen Processing', 'Lab Equipment'],
    programsCompleted: ['MLT Associate Degree', 'Clinical Lab Certification'],
    featured: true,
    likes: 176,
  },
  {
    id: 'story-5',
    employeeName: 'Sarah L.',
    initials: 'SL',
    avatarColor: 'from-violet-400 to-purple-500',
    fromRole: 'Staff RN (ICU)',
    toRole: 'Nurse Manager',
    department: 'Intensive Care Unit',
    timeframe: '4 years',
    salaryIncrease: '+45%',
    quote:
      'Career Pathways mapped out exactly what I needed: BSN completion, leadership training, and operational experience. The gap analysis kept me focused. Best decision I ever made!',
    skillsGained: [
      'Budget Management',
      'Staff Development',
      'Quality Improvement',
      'Strategic Planning',
    ],
    programsCompleted: [
      'BSN Completion',
      'Nurse Leadership Certificate',
      'Healthcare Management Certificate',
    ],
    featured: true,
    likes: 423,
  },
];

// ============================================================================
// RECOMMENDATIONS
// ============================================================================

export interface Recommendation {
  id: string;
  title: string;
  provider: string;
  type: 'Certificate' | 'Certification' | 'Degree' | 'Course' | 'Training';
  duration: string;
  cost: string;
  taEligible: boolean;
  matchScore: number;
  skillsGained: string[];
  salaryImpact: string;
  completionRate: number;
  reason: string;
  badge?: string;
  isTopPick: boolean;
}

export const RECOMMENDATIONS: Recommendation[] = [
  {
    id: 'rec-1',
    title: 'Nurse Leadership Certificate',
    provider: 'American Nurses Credentialing Center (ANCC)',
    type: 'Certificate',
    duration: '4 months',
    cost: '$1,850',
    taEligible: true,
    matchScore: 98,
    skillsGained: ['Leadership', 'Team Management', 'Quality Improvement', 'Conflict Resolution'],
    salaryImpact: '+$12,000',
    completionRate: 88,
    reason:
      'Perfect match for your Nurse Manager goal. Covers leadership skills gap identified in your profile.',
    isTopPick: true,
    badge: 'Best Match',
  },
  {
    id: 'rec-2',
    title: 'Epic Clinical Documentation Certification',
    provider: 'Epic Systems',
    type: 'Certification',
    duration: '6 weeks',
    cost: '$450',
    taEligible: true,
    matchScore: 95,
    skillsGained: ['Epic EMR', 'Clinical Documentation', 'Workflow Optimization'],
    salaryImpact: '+$8,000',
    completionRate: 92,
    reason: 'High priority skill at your hospital. Opens door to Informatics roles.',
    badge: 'Hospital Priority',
    isTopPick: false,
  },
  {
    id: 'rec-3',
    title: 'BSN Completion Program (RN-to-BSN)',
    provider: 'University Partners Network',
    type: 'Degree',
    duration: '18 months',
    cost: '$12,000',
    taEligible: true,
    matchScore: 92,
    skillsGained: ['Evidence-Based Practice', 'Research', 'Leadership', 'Public Health'],
    salaryImpact: '+$15,000',
    completionRate: 85,
    reason: 'Required for management track. 100% online, designed for working nurses.',
    badge: 'Career Essential',
    isTopPick: false,
  },
  {
    id: 'rec-4',
    title: 'Charge Nurse Training Program',
    provider: 'Hospital Internal Learning',
    type: 'Training',
    duration: '8 weeks',
    cost: 'Free',
    taEligible: false,
    matchScore: 90,
    skillsGained: ['Shift Management', 'Staffing', 'Incident Response', 'Delegation'],
    salaryImpact: '+$6,000',
    completionRate: 95,
    reason: 'Next step on your leadership path. Internal program, no cost to you.',
    badge: 'Quick Win',
    isTopPick: false,
  },
  {
    id: 'rec-5',
    title: 'Healthcare Management Certificate',
    provider: 'Cornell University (eCornell)',
    type: 'Certificate',
    duration: '6 months',
    cost: '$3,600',
    taEligible: true,
    matchScore: 85,
    skillsGained: [
      'Operations Management',
      'Healthcare Finance',
      'Quality Management',
      'Strategic Planning',
    ],
    salaryImpact: '+$18,000',
    completionRate: 82,
    reason: 'Prestigious program for executive track. Goes beyond typical nursing leadership.',
    badge: 'Executive Track',
    isTopPick: false,
  },
];

// ============================================================================
// STATISTICS & SUMMARY
// ============================================================================

export interface CareerPathwaysSummary {
  totalGaps: number;
  criticalGaps: number;
  avgTimeToClose: string;
  totalLearningOptions: number;
  taEligibleOptions: number;
  maxTaCoverage: string;
  skillsTracked: number;
  skillsVerified: number;
  skillsInProgress: number;
  profileCompleteness: number;
}

export const SUMMARY: CareerPathwaysSummary = {
  totalGaps: 3,
  criticalGaps: 1,
  avgTimeToClose: '3 months',
  totalLearningOptions: 9,
  taEligibleOptions: 6,
  maxTaCoverage: '$5,250/year',
  skillsTracked: 12,
  skillsVerified: 3,
  skillsInProgress: 2,
  profileCompleteness: 78,
};
